import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmailEditor } from '@/components/EmailEditor';
import { 
  FileText, 
  Bell, 
  Heart, 
  RotateCcw, 
  AlertTriangle, 
  Building
} from 'lucide-react';

interface MerchantEmailTemplate {
  id: string;
  type: string;
  subject: string;
  content: string;
  enabled: boolean;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// Define merchant-specific email template types
const MERCHANT_EMAIL_TYPES = [
  {
    id: 'welcome',
    name: 'Welcome',
    icon: Heart,
    description: 'Welcome new merchants to the platform',
    defaultSubject: 'You Can Now Accept Credit Cards!',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>Welcome to our payment platform! We're excited to have you as a partner.</p>
      <p>You're all set to accept credit cards on our platform.</p>
      <p>If you have any questions, please don't hesitate to reach out to our support team.</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  },
  {
    id: 'application-received',
    name: 'Application Received',
    icon: FileText,
    description: 'Confirm receipt of merchant application',
    defaultSubject: 'We\'re Setting Up Your Payment Account',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>We're setting up your payment account and it is now being processed.</p>
      <p>Our team is working on your setup and we'll contact you within 2-3 business days.</p>
      <p>Account Details:</p>
      <ul>
        <li>Business Name: {{businessName}}</li>
        <li>Setup Date: {{applicationDate}}</li>
        <li>Status: {{applicationStatus}}</li>
      </ul>
      <p>Thank you for choosing our platform.</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  },
  {
    id: 'underwriting-update',
    name: 'Underwriting Update',
    icon: Bell,
    description: 'Notify merchants of underwriting status changes',
    defaultSubject: 'Payment Account Update',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>We have an update on your payment account setup.</p>
      <p>Status: {{applicationStatus}}</p>
      <p>{{updateMessage}}</p>
      <p>If you have any questions, please contact your account manager at {{contactEmail}}.</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  },
  {
    id: 'documents-required',
    name: 'Documents Required',
    icon: AlertTriangle,
    description: 'Request additional documentation from merchants',
    defaultSubject: 'We Need a Few More Details',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>To complete your payment account setup, we need the following additional documents:</p>
      <p>{{requiredDocuments}}</p>
      <p>Please upload these documents through your business portal or reply to this email with the attachments.</p>
      <p>Once we receive the required documentation, we'll continue setting up your account.</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  },
  {
    id: 'approved',
    name: 'Application Approved',
    icon: Heart,
    description: 'Notify merchants of application approval',
    defaultSubject: 'Great News! You Can Now Accept Credit Cards',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>Great news! You can now accept credit cards.</p>
      <p>You're all set to accept payments on our platform. Your banking partner is {{bankingPartner}}.</p>
      <p>Next steps:</p>
      <ul>
        <li>Log into your business portal</li>
        <li>Complete your payment setup</li>
        <li>Start accepting payments</li>
      </ul>
      <p>Welcome to our platform!</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  },
  {
    id: 'declined',
    name: 'Application Declined',
    icon: AlertTriangle,
    description: 'Notify merchants of application decline',
    defaultSubject: 'We Need More Information',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>Thank you for your interest in accepting payments through our platform.</p>
      <p>We need a bit more information to complete your payment setup.</p>
      <p>{{declineReason}}</p>
      <p>Please contact us to discuss how we can move forward with your account setup.</p>
      <p>Thank you for choosing our platform.</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  },
  {
    id: 'banking-partner-assigned',
    name: 'Banking Partner Assigned',
    icon: Building,
    description: 'Notify merchants when banking partner is assigned',
    defaultSubject: 'Your Payment Account is Ready',
    defaultContent: `
      <p>Dear {{merchantName}},</p>
      <p>We're pleased to inform you that your payment account has been set up with our banking partner: {{bankingPartner}}.</p>
      <p>Your banking partner will handle your payments and provide ongoing support for your transactions.</p>
      <p>Banking Partner Details:</p>
      <ul>
        <li>Bank Name: {{bankingPartner}}</li>
        <li>Contact: {{bankContact}}</li>
        <li>Support Email: {{bankSupportEmail}}</li>
      </ul>
      <p>You'll receive additional setup instructions from {{bankingPartner}} shortly.</p>
      <p>Best regards,<br>{{platformName}}</p>
    `
  }
];

// Template variables for merchant communications
export function MerchantEmailTemplates() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplates, setEditingTemplates] = useState<Record<string, { subject: string; content: string }>>({});
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch merchant email templates from global collection
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['merchant-email-templates'],
    queryFn: async () => {
      const templatesRef = collection(db, 'merchantEmailTemplates');
      const snapshot = await getDocs(templatesRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MerchantEmailTemplate[];
    },
  });

  // Auto-sync templates on mount (only if they don't exist)
  React.useEffect(() => {
    const syncTemplates = async () => {
      if (isLoading || isSyncing || !user) return;

      try {
        setIsSyncing(true);

        // Get existing template types
        const existingTypes = templates.map(t => t.type);

        // Find missing templates
        const missingTemplates = MERCHANT_EMAIL_TYPES.filter(
          templateType => !existingTypes.includes(templateType.id)
        );

        if (missingTemplates.length > 0) {
          console.log(`[Global] Syncing ${missingTemplates.length} missing email templates...`);

          // Create missing templates in parallel in global collection
          const syncPromises = missingTemplates.map(async (templateType) => {
            const templateRef = doc(db, 'merchantEmailTemplates', templateType.id);
            await setDoc(templateRef, {
              type: templateType.id,
              subject: templateType.defaultSubject,
              content: templateType.defaultContent,
              enabled: true,
              updatedAt: new Date(),
              updatedBy: user.uid
            });
          });

          await Promise.all(syncPromises);

          // Refresh the templates list
          queryClient.invalidateQueries({ queryKey: ['merchant-email-templates'] });

          console.log(`[Global] Successfully synced ${missingTemplates.length} email templates`);
        }
      } catch (error) {
        console.error('[Global] Error syncing email templates:', error);
      } finally {
        setIsSyncing(false);
      }
    };

    // Only sync if templates have been loaded
    if (!isLoading) {
      syncTemplates();
    }
  }, [templates, isLoading, isSyncing, user, queryClient]);

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: async ({ type, subject, content, enabled }: {
      type: string;
      subject: string;
      content: string;
      enabled: boolean;
    }) => {
      const templateRef = doc(db, 'merchantEmailTemplates', type);
      await setDoc(templateRef, {
        type,
        subject,
        content,
        enabled,
        updatedAt: new Date(),
        updatedBy: user?.uid
      }, { merge: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-email-templates'] });
      toast({
        title: 'Template saved',
        description: 'Email template has been updated successfully.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to save email template.',
        variant: 'destructive',
      });
    },
  });

  const getTemplate = (type: string) => {
    return templates.find(t => t.type === type);
  };

  const handleSaveTemplate = (type: string, subject: string, content: string, enabled: boolean = true) => {
    saveTemplateMutation.mutate({ type, subject, content, enabled });
  };

  const getEditingTemplate = (templateId: string) => {
    const existingTemplate = getTemplate(templateId);
    const defaultTemplate = MERCHANT_EMAIL_TYPES.find(t => t.id === templateId);
    
    return editingTemplates[templateId] || {
      subject: existingTemplate?.subject || defaultTemplate?.defaultSubject || '',
      content: existingTemplate?.content || defaultTemplate?.defaultContent || ''
    };
  };

  const updateEditingTemplate = (templateId: string, updates: Partial<{ subject: string; content: string }>) => {
    setEditingTemplates(prev => ({
      ...prev,
      [templateId]: {
        ...getEditingTemplate(templateId),
        ...updates
      }
    }));
  };

  if (isLoading) {
    return <div>Loading email templates...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="welcome" className="w-full">
        <div className="relative mb-6">
          <TabsList className="bg-gray-50 h-auto p-2 rounded-xl flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400 scroll-smooth">
            {MERCHANT_EMAIL_TYPES.map((templateType) => {
              const Icon = templateType.icon;
              return (
                <TabsTrigger
                  key={templateType.id}
                  value={templateType.id}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-600 data-[state=inactive]:text-gray-600 hover:text-gray-900 transition-all duration-200 whitespace-nowrap flex-shrink-0"
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium text-sm">{templateType.name}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {MERCHANT_EMAIL_TYPES.map((templateType) => {
          const existingTemplate = getTemplate(templateType.id);
          
          return (
            <TabsContent key={templateType.id} value={templateType.id} className="mt-0">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-xl p-6 border border-gray-200/50">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-white rounded-lg shadow-sm border border-gray-200/50">
                        <templateType.icon className="h-5 w-5 text-gray-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{templateType.name} Email Template</h3>
                        <p className="text-sm text-gray-600 mt-0.5">{templateType.description}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        updateEditingTemplate(templateType.id, {
                          subject: templateType.defaultSubject,
                          content: templateType.defaultContent
                        });
                      }}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset to Default
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <EmailEditor
                      key={`editor-${templateType.id}-${existingTemplate?.id || 'default'}`}
                      content={getEditingTemplate(templateType.id).content}
                      onChange={(content) => updateEditingTemplate(templateType.id, { content })}
                      subject={getEditingTemplate(templateType.id).subject}
                      onSubjectChange={(subject) => updateEditingTemplate(templateType.id, { subject })}
                      companyId="platform"
                      paymentUrl="https://example.com/payment"
                    />
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>Last updated:</span>
                        <span className="font-medium">
                          {existingTemplate?.updatedAt 
                            ? new Date(existingTemplate.updatedAt.toDate()).toLocaleDateString()
                            : 'Never'
                          }
                        </span>
                      </div>
                      <Button 
                        onClick={() => {
                          const template = getEditingTemplate(templateType.id);
                          handleSaveTemplate(templateType.id, template.subject, template.content);
                        }}
                        disabled={saveTemplateMutation.isPending}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm"
                      >
                        {saveTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
